import React, { useRef } from "react";
import { motion, useInView, useMotionValue, useTransform } from "framer-motion";
import { EASE, fadeUpNoBlur } from "../../lib/motion";
import { resolveImg } from "../../lib/image";

const FounderCard = ({ founder, index }) => {
    const wrapperRef = useRef(null);
    const inView = useInView(wrapperRef, { once: false, margin: "-80px" });

    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    // Softer tilt range (±2°) for a gentler, more premium feel and lighter GPU cost
    const rx = useTransform(my, [-50, 50], [2, -2]);
    const ry = useTransform(mx, [-50, 50], [-2, 2]);

    const handleMove = (e) => {
        if (!inView) return;
        const rect = wrapperRef.current?.getBoundingClientRect();
        if (!rect) return;
        mx.set(e.clientX - rect.left - rect.width / 2);
        my.set(e.clientY - rect.top - rect.height / 2);
    };
    const handleLeave = () => { mx.set(0); my.set(0); };

    return (
        <motion.div
            ref={wrapperRef}
            {...fadeUpNoBlur(index * 0.08)}
            className="will-change-transform"
            style={{ perspective: 800 }}
            data-testid={`founder-card-${index}`}
        >
            <motion.div
                onMouseMove={handleMove}
                onMouseLeave={handleLeave}
                style={{
                    rotateX: rx,
                    rotateY: ry,
                    transformStyle: "preserve-3d",
                    willChange: "transform",
                    transform: "translateZ(0)",
                }}
                whileHover={{ y: -4 }}
                transition={{ type: "spring", stiffness: 200, damping: 24 }}
                className="surface-card overflow-hidden group"
            >
                <div className="relative aspect-[4/5] overflow-hidden">
                    <img
                        src={resolveImg(founder.image)}
                        alt={founder.name}
                        loading="lazy"
                        decoding="async"
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1600ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.04]"
                        style={{ filter: "grayscale(15%) saturate(85%)" }}
                    />
                    <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 60%, rgba(0,0,0,0.45) 100%)" }} />
                </div>
                <div className="p-8 md:p-10">
                    <div className="text-xs uppercase tracking-[0.22em] text-oth-ink3 font-body">{founder.role}</div>
                    <div className="mt-3 font-display text-3xl md:text-4xl font-semibold tracking-tight">{founder.name}</div>
                    <p className="mt-5 font-body text-base md:text-lg text-oth-ink2 leading-relaxed max-w-[440px] whitespace-pre-wrap">{founder.story}</p>
                </div>
            </motion.div>
        </motion.div>
    );
};

export const AboutUs = ({ content }) => {
    const a = content?.about || {};
    return (
        <section id="about" className="relative py-6 md:py-20" data-testid="about-section">
            <div className="mx-auto max-w-[1240px] px-6 md:px-10">
                <div className="mb-8 max-w-[760px]">
                    <motion.span {...fadeUpNoBlur(0)} className="text-xs uppercase tracking-[0.24em] text-oth-ink3 font-body">{a.eyebrow}</motion.span>
                    <motion.h2 {...fadeUpNoBlur(0.08)} className="mt-4 font-display font-semibold text-[40px] sm:text-[52px] md:text-[64px] leading-[1.02] tracking-[-0.035em]">
                        {a.headline}
                    </motion.h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                    {(a.founders || []).map((f, i) => <FounderCard key={`founder-${f.name || i}`} founder={f} index={i} />)}
                </div>
            </div>
        </section>
    );
};
